import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { Button } from '@/ui/Button';
import { MODULES, type ModuleId } from './catalog';
import { ModuleContext } from './ModuleContext';

export function validateModules(value: unknown): ModuleId[] {
  if (typeof value !== 'object' || value === null || !('phase' in value) || !('modules' in value)) {
    throw new Error('Invalid release configuration');
  }
  const phase = value.phase;
  if (
    typeof phase !== 'number' ||
    !Number.isInteger(phase) ||
    phase < 1 ||
    phase > 4 ||
    !Array.isArray(value.modules)
  ) {
    throw new Error('Invalid release configuration');
  }
  const modules = value.modules;
  const expected = (Object.keys(MODULES) as ModuleId[]).filter((id) => MODULES[id].phase <= phase);
  if (value.modules.length !== expected.length || !expected.every((id) => modules.includes(id))) {
    throw new Error('Release configuration does not match this application');
  }
  return expected;
}

const CACHE_KEY = 'fieldops.release-modules.v1';

/** Last known installation policy is only an offline UI hint; the server always enforces writes. */
export async function loadReleaseModules(): Promise<ModuleId[]> {
  let result: Awaited<ReturnType<typeof readRelease>>;
  try {
    result = await readRelease();
  } catch (error) {
    // A network failure may use previously verified metadata on an offline field handset.
    // HTTP errors and malformed successful responses never reach this fallback.
    if (error instanceof TypeError && window.location.pathname.startsWith('/field')) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) return validateModules(JSON.parse(cached));
      } catch {
        /* Missing or corrupt cache fails closed. */
      }
    }
    throw error;
  }
  const { data, response } = result;
  if (!response.ok) throw new Error('Release configuration could not be loaded');
  const modules = validateModules(data);
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    /* Online use does not require storage. */
  }
  return modules;
}

function readRelease() {
  return apiClient.GET('/api/release-modules');
}

/** Load server policy before any page, query, public link or offline queue can mount. */
export function ModuleProvider({ children }: { children: React.ReactNode }) {
  const release = useQuery({
    queryKey: ['release-modules'],
    queryFn: loadReleaseModules,
    networkMode: 'always',
    staleTime: Infinity,
    retry: 1,
  });
  if (release.isPending) return <main role="status">Loading workspace…</main>;
  if (release.error)
    return (
      <main>
        <h1>Workspace unavailable</h1>
        <p>Release configuration could not be loaded.</p>
        <Button onClick={() => void release.refetch()}>Retry</Button>
      </main>
    );
  return <ModuleContext value={release.data}>{children}</ModuleContext>;
}
